import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
function toDateSafe(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v === "object" && typeof v.toDate === "function") return v.toDate(); // Firestore Timestamp
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtGBP(n) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function ymd(d) {
  const dt = toDateSafe(d);
  if (!dt) return "";
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}

function monthKeyFromDate(d) {
  const dt = toDateSafe(d);
  if (!dt) return "";
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}`; // YYYY-MM
}

function weekKey(d) {
  const dt = toDateSafe(d);
  if (!dt) return "";
  // Monday-based week start (UK-friendly)
  const x = new Date(dt);
  const day = x.getDay(); // 0=Sun..6=Sat
  const diff = (day === 0 ? -6 : 1) - day; // move to Monday
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return `${x.getFullYear()}-${pad2(x.getMonth() + 1)}-${pad2(x.getDate())}`; // "YYYY-MM-DD" of Monday
}


function daysBetween(start, end) {
  if (!start || !end) return 0;
  const ms = toDateSafe(end).getTime() - toDateSafe(start).getTime();
  return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
}

function weeksInPeriodActual(start, end) {
  // end is exclusive in your periodRange
  const days = daysBetween(start, end);
  return days > 0 ? days / 7 : 0;
}

function shiftHoursValue(s) {
  // use whatever you store; fallback chain keeps it robust
  return safeNum(s.totalHours ?? s.hours ?? s.plannedHours ?? s.durationHours);
}

// Status buckets (edit these strings to match your Firestore status values)
const STATUS_CHECKED_OUT = new Set(["checked out", "completed"]);
const STATUS_EXCLUDED_FROM_ASSIGNED = new Set(["cancelled", "canceled", "declined", "rejected", "no show"]);








function inMonth(objDate, monthKey) {
  if (!monthKey) return true;
  return monthKeyFromDate(objDate) === monthKey;
}


function startOfMonth(yyyyMm) {
  const [y, m] = String(yyyyMm).split("-").map(Number);
  return new Date(y, (m || 1) - 1, 1, 0, 0, 0, 0);
}
function endOfMonthExclusive(yyyyMm) {
  const [y, m] = String(yyyyMm).split("-").map(Number);
  return new Date(y, m || 1, 1, 0, 0, 0, 0); // next month 1st day
}
function startOfYear(yyyy) {
  return new Date(Number(yyyy), 0, 1, 0, 0, 0, 0);
}
function endOfYearExclusive(yyyy) {
  return new Date(Number(yyyy) + 1, 0, 1, 0, 0, 0, 0);
}

function getPeriodRange(periodMode, monthFilter, yearFilter) {
  if (periodMode === "all") return { start: null, end: null };
  if (periodMode === "year") return { start: startOfYear(yearFilter), end: endOfYearExclusive(yearFilter) };
  // month
  return { start: startOfMonth(monthFilter), end: endOfMonthExclusive(monthFilter) };
}

function inRange(objDate, start, end) {
  const dt = toDateSafe(objDate);
  if (!dt) return false;
  if (!start || !end) return true; // All mode
  const t = dt.getTime();
  return t >= start.getTime() && t < end.getTime();
}






function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function classNames(...arr) {
  return arr.filter(Boolean).join(" ");
}

// ─────────────────────────────────────────────────────────────
// Reports Page
// ─────────────────────────────────────────────────────────────
export default function Reports() {
  // Tabs
  const TABS = [
    { key: "invoices", label: "Invoices" },
    { key: "expenses", label: "Expenses" },
	  { key: "cashflow", label: "Cashflow" },
    { key: "profit", label: "Profit" },
    { key: "clients", label: "Clients" },
    { key: "operations", label: "Staff" },
    { key: "payroll", label: "Staff Payroll" },
	
    { key: "exports", label: "Exports" },
  ];
  const [activeTab, setActiveTab] = useState("invoices");

  // Filters
  const [monthFilter, setMonthFilter] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}`;
  });
  
  
  const [periodMode, setPeriodMode] = useState("month"); // "month" | "year" | "all"
  const [yearFilter, setYearFilter] = useState(() => String(new Date().getFullYear()));

  const [clientFilter, setClientFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchInvoice, setSearchInvoice] = useState("");

  // Data
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  
  const periodRange = useMemo(() => getPeriodRange(periodMode, monthFilter, yearFilter), [periodMode, monthFilter, yearFilter]);
  const [shifts, setShifts] = useState([]);

  //const [staff, setStaff] = useState([]);

  const [shiftsData, setShiftsData] = useState([]);
  const [staffData, setStaffData] = useState([]);




    const staffPayrollSummary = useMemo(() => {
  const summaryMap = {};

  // 1. Initialize keys using lowercase, but preserve the exact display name
  if (Array.isArray(staffData)) {
    staffData.forEach((s) => {
      if (s.fullName) {
        const key = s.fullName.trim().toLowerCase(); // Normalize string spacing and casing
        summaryMap[key] = {
          name: s.fullName, // Keep beautiful display name
          checkedOutShiftsCount: 0,
          totalHours: 0,
          totalPay: 0,
        };
      }
    });
  }

  // 2. Process shifts using the exact same lowercase key matching logic
  if (Array.isArray(shiftsData)) {
    shiftsData.forEach((shift) => {


      // Get the shift anchor date (fallback to checkOutDate or createdAt if needed)
      const shiftDate = shift.date || shift.checkOutDate || shift.createdAt;

      // --- ADDED DATE RANGE FILTER CHECK ---
      const matchesPeriod = inRange(shiftDate, periodRange.start, periodRange.end);
      if (shift.status === "Checked Out" && shift.staffName && matchesPeriod) {
        const key = shift.staffName.trim().toLowerCase(); // Match normalization exactly
        
        // Fallback fallback selector if the staff wasn't found in staffData
        if (!summaryMap[key]) {
          summaryMap[key] = {
            name: shift.staffName,
            checkedOutShiftsCount: 0,
            totalHours: 0,
            totalPay: 0,
          };
        }

        const hours = Number(shift.totalHours) || 0;
        summaryMap[key].checkedOutShiftsCount += 1;
        summaryMap[key].totalHours += hours;
        summaryMap[key].totalPay += hours * 13;
      }
    });
  }

  // Convert map to sorted presentation array
  return Object.values(summaryMap).sort((a, b) => a.name.localeCompare(b.name));
}, [shiftsData, staffData, periodRange]);
  
  


  // Expenses form (simple admin entry)
  const [expDate, setExpDate] = useState(() => ymd(new Date()));
  const [expVendor, setExpVendor] = useState("");
  const [expCategory, setExpCategory] = useState("Software");
  const [expDescription, setExpDescription] = useState("");
  const [expAmount, setExpAmount] = useState("");
  const [expSaving, setExpSaving] = useState(false);
  const [expMsg, setExpMsg] = useState({ ok: "", err: "" });

  // Load data
  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");
      try {
        // Clients
        const clientsSnap = await getDocs(collection(db, "clients"));
        const clientList = clientsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        clientList.sort((a, b) =>
          String(a.name || a.clientName || a.fullName || "").localeCompare(
            String(b.name || b.clientName || b.fullName || "")
          )
        );
        setClients(clientList);
		
     // --- FIX STARTS HERE ---
      // Fetch shifts and set BOTH shifts and shiftsData
      const shiftsSnap = await getDocs(collection(db, "shifts"));
      const shiftsList = shiftsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      setShifts(shiftsList);     // Used for overall staff analytics tab
      setShiftsData(shiftsList); // Used for your Staff Payroll tab summary!
      // --- FIX ENDS HERE ---   
		//const shiftsSnap = await getDocs(collection(db, "shifts"));
		//setShifts(shiftsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

		
		
        // Invoices (order by createdAt if possible)
        try {
          const invQ = query(collection(db, "invoices"), orderBy("createdAt", "desc"));
          const invSnap = await getDocs(invQ);
          const invList = invSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setInvoices(invList);
        } catch {
          // fallback if createdAt ordering/index missing
          const invSnap = await getDocs(collection(db, "invoices"));
          const invList = invSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
          invList.sort((a, b) => String(b.invoiceNumber || "").localeCompare(String(a.invoiceNumber || "")));
          setInvoices(invList);
        }

        // Expenses (you can create this collection from the UI)
        try {
          const expQ = query(collection(db, "expenses"), orderBy("date", "desc"));
          const expSnap = await getDocs(expQ);
          const expList = expSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setExpenses(expList);
        } catch {
          const expSnap = await getDocs(collection(db, "expenses"));
          const expList = expSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
          expList.sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
          setExpenses(expList);
        }
      } catch (e) {
        console.error(e);
        setErr("Failed to load reports data. Check console.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const clientNameById = useMemo(() => {
    const map = new Map();
    clients.forEach((c) => {
      const name = c.name || c.clientName || c.fullName || c.id;
      map.set(c.id, name);
    });
    return map;
  }, [clients]);

  const invoiceClientOptions = useMemo(() => {
    // Unique clients from invoices for dropdown
    const set = new Map();
    invoices.forEach((inv) => {
      const id = inv.clientId || "";
      const name = inv.clientName || inv.invoiceFor || (id ? clientNameById.get(id) : "");
      if (id && name) set.set(id, name);
    });
    return Array.from(set.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [invoices, clientNameById]);

  // ─────────────────────────────────────────────────────────────
  // Derived: filtered invoices
  // ─────────────────────────────────────────────────────────────
  const filteredInvoices = useMemo(() => {
    const q = String(searchInvoice || "").trim().toLowerCase();

    return invoices
      .filter((inv) => {
        const created = inv.createdAt || inv.submittedOn || inv.period?.fromDate || inv.period?.toDate;
		if (!inRange(created, periodRange.start, periodRange.end)) return false;


        if (clientFilter !== "all") {
          if ((inv.clientId || "") !== clientFilter) return false;
        }

        if (statusFilter !== "all") {
          const st = String(inv.status || "Draft").toLowerCase();
          const paid = !!inv.paid;
          if (statusFilter === "paid") {
            if (!paid) return false;
          } else if (statusFilter === "unpaid") {
            if (paid) return false;
          } else {
            if (st !== statusFilter) return false;
          }
        }

        if (q) {
          const hay = `${inv.invoiceNumber || ""} ${inv.clientName || ""} ${inv.invoiceFor || ""}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }

        return true;
      })
      .sort((a, b) => String(b.invoiceNumber || "").localeCompare(String(a.invoiceNumber || "")));
  }, [invoices, periodRange, clientFilter, statusFilter, searchInvoice]);

  const invoiceTotals = useMemo(() => {
    const totalInvoiced = filteredInvoices.reduce((sum, inv) => sum + safeNum(inv.total ?? inv.subtotal), 0);
    const totalPaid = filteredInvoices.reduce(
      (sum, inv) => sum + (inv.paid ? safeNum(inv.total ?? inv.subtotal) : 0),
      0
    );
    const totalOutstanding = Math.max(0, totalInvoiced - totalPaid);
    return { totalInvoiced, totalPaid, totalOutstanding };
  }, [filteredInvoices]);

  // ─────────────────────────────────────────────────────────────
  // Derived: filtered expenses
  // ─────────────────────────────────────────────────────────────
  const filteredExpenses = useMemo(() => {
    return expenses
      .filter((e) => inRange(e.date || e.createdAt, periodRange.start, periodRange.end))
      .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
  }, [expenses, periodRange]);

const selectedYearForYtd = useMemo(() => {
  if (periodMode === "year") return Number(yearFilter);
  if (periodMode === "month") return Number(String(monthFilter).split("-")[0]);
  // all mode → default to current year for YTD cards
  return new Date().getFullYear();
}, [periodMode, yearFilter, monthFilter]);





  const expenseTotals = useMemo(() => {
    return filteredExpenses.reduce((sum, e) => sum + safeNum(e.amount), 0);
  }, [filteredExpenses]);

  // Profit
  const profitSummary = useMemo(() => {
    const revenue = invoiceTotals.totalInvoiced;
    const expensesTotal = expenseTotals;
    const profit = revenue - expensesTotal;
    const margin = revenue > 0 ? (profit / revenue) * 100 : 0;

    // YTD (based on the selected year)
    const selectedYear = selectedYearForYtd;


    const ytdRevenue = invoices
      .filter((inv) => {
        const anchor = inv.createdAt || inv.period?.fromDate || inv.submittedOn;
        const dt = toDateSafe(anchor);
        return dt && dt.getFullYear() === selectedYear;
      })
      .reduce((sum, inv) => sum + safeNum(inv.total ?? inv.subtotal), 0);

    const ytdExpenses = expenses
      .filter((e) => {
        const dt = toDateSafe(e.date || e.createdAt);
        return dt && dt.getFullYear() === selectedYear;
      })
      .reduce((sum, e) => sum + safeNum(e.amount), 0);

    const ytdProfit = ytdRevenue - ytdExpenses;
    const ytdMargin = ytdRevenue > 0 ? (ytdProfit / ytdRevenue) * 100 : 0;

    return {
      revenue,
      expenses: expensesTotal,
      profit,
      margin,
      selectedYear,
      ytdRevenue,
      ytdExpenses,
      ytdProfit,
      ytdMargin,
    };
  }, [invoiceTotals, expenseTotals, invoices, expenses, selectedYearForYtd]);


// ─────────────────────────────────────────────────────────────
// Staff / Shift analytics
// ─────────────────────────────────────────────────────────────
const completedShiftsInMonth = useMemo(() => {
  return shifts.filter((s) => {
    const st = String(s.status || "").toLowerCase();
    const isCompleted = st === "checked out" || st === "completed";
    if (!isCompleted) return false;
    return inRange(s.date || s.checkOutDate || s.createdAt, periodRange.start, periodRange.end);
  });

}, [shifts, periodRange.start, periodRange.end]);


const weeksActualInPeriod = useMemo(() => {
  if (!periodRange.start || !periodRange.end) return 0; // all-mode
  const days = (periodRange.end.getTime() - periodRange.start.getTime()) / (1000 * 60 * 60 * 24);
  return days / 7; // e.g. 31/7 = 4.428...
}, [periodRange.start, periodRange.end]);






const shiftHoursInMonth = useMemo(() => {
  return completedShiftsInMonth.reduce((sum, s) => sum + safeNum(s.totalHours), 0);
}, [completedShiftsInMonth]);


const assignedShiftsInPeriod = useMemo(() => {
  return shifts.filter((s) => {
    const st = String(s.status || "").toLowerCase().trim();

    // "Assigned" = everything in-range that isn't explicitly cancelled/declined/etc.
    if (STATUS_EXCLUDED_FROM_ASSIGNED.has(st)) return false;

    // Must be in the currently selected period (month/year/all)
    return inRange(s.date || s.checkOutDate || s.createdAt, periodRange.start, periodRange.end);
  });
}, [shifts, periodRange.start, periodRange.end]);

const assignedHoursInPeriod = useMemo(() => {
  return assignedShiftsInPeriod.reduce((sum, s) => sum + shiftHoursValue(s), 0);
}, [assignedShiftsInPeriod]);


const weeksWithAssignedInPeriod = useMemo(() => {
  const set = new Set();
  assignedShiftsInPeriod.forEach((s) => {
    const dt = s.date || s.checkOutDate || s.createdAt;
    const wk = weekKey(dt);
    if (wk) set.add(wk);
  });
  return set.size;
}, [assignedShiftsInPeriod]);

const weeksWithDeliveredInPeriod = useMemo(() => {
  const set = new Set();
  completedShiftsInMonth.forEach((s) => {
    const dt = s.date || s.checkOutDate || s.createdAt;
    const wk = weekKey(dt);
    if (wk) set.add(wk);
  });
  return set.size;
}, [completedShiftsInMonth]);








const denomAssigned =
  periodMode === "year" || periodMode === "all"
    ? weeksWithAssignedInPeriod
    : weeksActualInPeriod;

const denomDelivered =
  periodMode === "year" || periodMode === "all"
    ? weeksWithDeliveredInPeriod
    : weeksActualInPeriod;


const avgAssignedHoursPerWeek = useMemo(() => {
  return denomAssigned > 0 ? assignedHoursInPeriod / denomAssigned : 0;
}, [assignedHoursInPeriod, denomAssigned]);

const avgDeliveredHoursPerWeek = useMemo(() => {
  return denomDelivered > 0 ? shiftHoursInMonth / denomDelivered : 0;
}, [shiftHoursInMonth, denomDelivered]);







const activeStaffThisMonth = useMemo(() => {
  const set = new Set();
  completedShiftsInMonth.forEach((s) => {
    if (s.staffId) set.add(s.staffId);
    else if (s.staffName) set.add(s.staffName); // fallback
  });
  return set.size;
}, [completedShiftsInMonth]);








// YTD
const staffYtdSummary = useMemo(() => {
  const selectedYear = selectedYearForYtd;

  // YTD start = Jan 1
  const start = new Date(selectedYear, 0, 1);

  // YTD end = end of selected period (month/year), otherwise "today"
  // (periodRange.end is already the exclusive end date)
  const end =
    periodMode === "month" || periodMode === "year"
      ? periodRange.end
      : new Date();

  const completedYtd = shifts.filter((s) => {
    const st = String(s.status || "").toLowerCase();
    const isCompleted = st === "checked out" || st === "completed";
    if (!isCompleted) return false;

    return inRange(s.date || s.checkOutDate || s.createdAt, start, end);
  });

  const hoursYtd = completedYtd.reduce((sum, s) => sum + safeNum(s.totalHours), 0);

  // ✅ Actual weeks elapsed in the YTD window (days ÷ 7)
  const days =
    (toDateSafe(end).getTime() - toDateSafe(start).getTime()) / (1000 * 60 * 60 * 24);
  const weeksActual = days > 0 ? days / 7 : 0;

  const avgHoursPerWeekYtd = weeksActual > 0 ? hoursYtd / weeksActual : 0;

  // Top staff by hours (YTD)
  const byStaff = new Map();
  completedYtd.forEach((s) => {
    const key = s.staffId || s.staffName || "Unknown";
    const cur = byStaff.get(key) || {
      staffId: s.staffId || null,
      staffName: s.staffName || "Unknown",
      hours: 0,
      shifts: 0,
    };
    cur.hours += safeNum(s.totalHours);
    cur.shifts += 1;
    byStaff.set(key, cur);
  });

  const topStaff = Array.from(byStaff.values())
    .sort((a, b) => b.hours - a.hours)
    .slice(0, 10);

  return {
    selectedYear,
    completedCount: completedYtd.length,
    hoursYtd,
    avgHoursPerWeekYtd,
    topStaff,
  };
}, [shifts, selectedYearForYtd, periodMode, periodRange.start, periodRange.end]);









  // ─────────────────────────────────────────────────────────────
  // Actions
  // ─────────────────────────────────────────────────────────────
  const toggleInvoicePaid = async (inv) => {
    const nextPaid = !inv.paid;

    // optimistic UI
    setInvoices((prev) => prev.map((x) => (x.id === inv.id ? { ...x, paid: nextPaid } : x)));

    try {
      await updateDoc(doc(db, "invoices", inv.id), {
        paid: nextPaid,
        paidAt: nextPaid ? serverTimestamp() : null,
        status: nextPaid ? "Paid" : inv.status || "Draft",
      });
    } catch (e) {
      console.error(e);
      // revert
      setInvoices((prev) => prev.map((x) => (x.id === inv.id ? { ...x, paid: !!inv.paid } : x)));
      alert("Failed to update invoice. Check console.");
    }
  };

  const addExpense = async () => {
    setExpMsg({ ok: "", err: "" });

    const amt = Number(expAmount);
    if (!expDate || !expCategory || !Number.isFinite(amt) || amt <= 0) {
      setExpMsg({ ok: "", err: "Please enter a valid date, category and amount." });
      return;
    }

    setExpSaving(true);
    try {
      const payload = {
        date: expDate,
        vendor: expVendor || null,
        category: expCategory,
        description: expDescription || null,
        amount: amt,
        createdAt: serverTimestamp(),
      };

      const ref = await addDoc(collection(db, "expenses"), payload);
      setExpenses((prev) => [{ id: ref.id, ...payload }, ...prev]);

      setExpVendor("");
      setExpDescription("");
      setExpAmount("");

      setExpMsg({ ok: "Expense saved.", err: "" });
    } catch (e) {
      console.error(e);
      setExpMsg({ ok: "", err: "Failed to save expense. Check console." });
    } finally {
      setExpSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Convalesce styling (same pattern as InvoiceManager)
  // ─────────────────────────────────────────────────────────────
  const pageWrap = { paddingTop: "20px" };
  const pageClass = "p-6 pt-20 min-h-screen bg-gray-700/80 text-gray-100";

  const headerPill = {
    textAlign: "center",
    color: "gray",
    backgroundColor: "white",
    boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
    width: "fit-content",
    margin: "10px auto 18px auto",
    fontSize: "16px",
    fontWeight: "500",
    padding: "14px 24px",
    borderRadius: "20px",
  };

  const card = {
    maxWidth: "1100px",
    margin: "18px auto",
    backgroundColor: "white",
    borderRadius: "16px",
    padding: "24px",
    color: "#1f2937",
    boxShadow: "0 4px 8px rgba(0,0,0,0.05)",
  };

  const innerCard = {
    border: "1px solid #e5e7eb",
    borderRadius: "16px",
    padding: "16px",
    backgroundColor: "#f9fafb",
  };

  const label = { fontSize: "12px", color: "#6b7280", fontWeight: 700, marginBottom: 6 };
  const input = {
    width: "100%",
    border: "1px solid #d1d5db",
    borderRadius: "10px",
    padding: "10px 12px",
    fontSize: "13px",
    outline: "none",
    backgroundColor: "#fff",
  };

  const btn = (bg) => ({
    padding: "10px 12px",
    borderRadius: "10px",
    backgroundColor: bg,
    color: "#fff",
    fontWeight: 700,
    border: "none",
    cursor: "pointer",
  });

  const statPill = (bg, fg) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    borderRadius: 14,
    background: bg,
    color: fg,
    fontWeight: 900,
    fontSize: 13,
  });
  
  
  const pickerCol = {
  minWidth: 130,
  maxWidth: 130,
  };

  
  
  

  const tabBtn = (active) => ({
    border: "1px solid #e5e7eb",
    background: active ? "#111827" : "#ffffff",
    color: active ? "#ffffff" : "#111827",
    fontWeight: 900,
    fontSize: 13,
    padding: "10px 12px",
    borderRadius: 12,
    cursor: "pointer",
  });

  const th = {
    textAlign: "left",
    fontSize: 12,
    color: "#1d4ed8",
    background: "#f9fafb",
    borderBottom: "1px solid #e5e7eb",
    padding: "10px 10px",
    fontWeight: 900,
    whiteSpace: "nowrap",
  };




  const td = {
    padding: "12px 10px",
    borderBottom: "1px solid #e5e7eb",
    fontSize: 13,
    color: "#111827",
    verticalAlign: "top",
  };

  if (loading) {
    return (
      <div style={pageWrap} className={pageClass}>
        <h1 style={headerPill}>Reports & Analytics</h1>
        <div style={card}>
          <div style={{ color: "#6b7280", fontWeight: 700 }}>Loading reports…</div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageWrap} className={pageClass}>
      <h1 style={headerPill}>Reports & Analytics</h1>

      {err && (
        <div style={card}>
          <div style={{ color: "#dc2626", fontWeight: 900 }}>❌ {err}</div>
        </div>
      )}

      {/* KPI strip */}
      <div style={card}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div style={statPill("#eff6ff", "#1d4ed8")}>Revenue: {fmtGBP(profitSummary.revenue)}</div>
            <div style={statPill("#fff7ed", "#9a3412")}>Expenses: {fmtGBP(profitSummary.expenses)}</div>
            <div style={statPill("#f0fdf4", "#166534")}>Profit: {fmtGBP(profitSummary.profit)}</div>
            <div style={statPill("#fdf2f8", "#9d174d")}>Margin: {profitSummary.margin.toFixed(1)}%</div>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
           


		   <div style={{ display: "flex", gap: 10, alignItems: "end", flexWrap: "wrap" }}>
			  <div>
				<div style={label}>Period</div>
				<div style={{ display: "flex", gap: 8 }}>
				  <button type="button" onClick={() => setPeriodMode("month")} style={tabBtn(periodMode === "month")}>Month</button>
				  <button type="button" onClick={() => setPeriodMode("year")} style={tabBtn(periodMode === "year")}>Year</button>
				  <button type="button" onClick={() => setPeriodMode("all")} style={tabBtn(periodMode === "all")}>All</button>
				</div>
			  </div>

			  
			  
			  
			 <div style={pickerCol}>
  {periodMode === "month" && (
    <>
      <div style={label}>Month</div>
      <input
        style={input}
        type="month"
        value={monthFilter}
        onChange={(e) => setMonthFilter(e.target.value)}
      />
    </>
  )}

  {periodMode === "year" && (
    <>
      <div style={label}>Year</div>
      <select
        style={input}
        value={yearFilter}
        onChange={(e) => setYearFilter(e.target.value)}
      >
        {Array.from({ length: 6 }).map((_, i) => {
          const y = new Date().getFullYear() - i;
          return (
            <option key={y} value={String(y)}>
              {y}
            </option>
          );
        })}
      </select>
    </>
  )}

  {periodMode === "all" && (
    <>
      <div style={label}>&nbsp;</div>
      <div
        style={{
          height: 38,
          border: "1px dashed #e5e7eb",
          borderRadius: 10,
          background: "#f9fafb",
        }}
      />
    </>
  )}
</div>
 
			  
			  
			  
			  
			  
			  
			  
			  
			  
			  
			</div>





            <button
              onClick={() => {
                // simple refresh (reload from Firestore)
                window.location.reload();
              }}
              style={btn("#6b7280")}
			  style={{ ...btn("#6b7280"), width: 110 }}
              title="Refresh"
            >
              Refresh
            </button>
          </div>
        </div>
		
		
		
		
		
		
		
      </div>

      {/* Tabs */}
      <div style={card}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={tabBtn(activeTab === t.key)}>
              {t.label}
            </button>
          ))}
        </div>

       

        <div style={{ marginTop: 16 }}>
          {/* INVOICES */}
          {activeTab === "invoices" && (
            <div style={innerCard}>
              <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr 1fr", gap: 12 }}>
                <div>
                  <div style={label}>Search invoice / client</div>
                  <input style={input} value={searchInvoice} onChange={(e) => setSearchInvoice(e.target.value)} placeholder="e.g. 0045 or Angela" />
                </div>

                <div>
                  <div style={label}>Client</div>
                  <select style={input} value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
                    <option value="all">All clients</option>
                    {invoiceClientOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div style={label}>Status</div>
                  <select style={input} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="all">All</option>
                    <option value="paid">Paid</option>
                    <option value="unpaid">Unpaid</option>
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="overdue">Overdue</option>
                  </select>
                </div>

                <div style={{ display: "flex", alignItems: "end", gap: 10 }}>
                  <button
                    onClick={() => {
                      setClientFilter("all");
                      setStatusFilter("all");
                      setSearchInvoice("");
                    }}
                    style={btn("#6b7280")}
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div style={{ marginTop: 16, overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 14, background: "#fff" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={th}>Paid</th>
                      <th style={th}>Invoice #</th>
                      <th style={th}>Client</th>
                      <th style={th}>Period</th>
                      <th style={th}>Submitted</th>
                      <th style={th}>Due</th>
                      <th style={{ ...th, textAlign: "right" }}>Subtotal</th>
                      <th style={{ ...th, textAlign: "right" }}>VAT</th>
                      <th style={{ ...th, textAlign: "right" }}>Total</th>
                      <th style={th}>Status</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredInvoices.map((inv, idx) => {
                      const zebra = idx % 2 === 0 ? "#ffffff" : "#f9fafb";
                      const period = inv.period ? `${inv.period.fromDate || "—"} → ${inv.period.toDate || "—"}` : "—";
                      return (
                        <tr key={inv.id} style={{ background: zebra }}>
                          <td style={td}>
                            <input type="checkbox" checked={!!inv.paid} onChange={() => toggleInvoicePaid(inv)} />
                          </td>
                          <td style={td}>{inv.invoiceNumber || "—"}</td>
                          <td style={td}>{inv.clientName || inv.invoiceFor || "—"}</td>
                          <td style={td}>{period}</td>
                          <td style={td}>{inv.submittedOn || "—"}</td>
                          <td style={td}>{inv.dueDate || "—"}</td>
                          <td style={{ ...td, textAlign: "right" }}>{fmtGBP(safeNum(inv.subtotal))}</td>
                          <td style={{ ...td, textAlign: "right" }}>{fmtGBP(safeNum(inv.vatAmount))}</td>
                          <td style={{ ...td, textAlign: "right", fontWeight: 900 }}>{fmtGBP(safeNum(inv.total ?? inv.subtotal))}</td>
                          <td style={td}>{inv.paid ? "Paid" : inv.status || "Draft"}</td>
                        </tr>
                      );
                    })}

                    {filteredInvoices.length === 0 && (
                      <tr>
                        <td colSpan={10} style={{ padding: 16, color: "#6b7280", fontWeight: 700 }}>
                          No invoices found for this filter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginTop: 14 }}>
                <div style={{ color: "#6b7280", fontWeight: 800, fontSize: 13 }}>
                  Showing <b>{filteredInvoices.length}</b> invoice(s) for <b>{periodMode === "all" ? "All time" : periodMode === "year" ? yearFilter : monthFilter}</b>

                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <div style={statPill("#eff6ff", "#1d4ed8")}>Total invoiced: {fmtGBP(invoiceTotals.totalInvoiced)}</div>
                  <div style={statPill("#f0fdf4", "#166534")}>Total paid: {fmtGBP(invoiceTotals.totalPaid)}</div>
                  <div style={statPill("#fff7ed", "#9a3412")}>Outstanding: {fmtGBP(invoiceTotals.totalOutstanding)}</div>
                </div>
              </div>
            </div>
          )}

          {/* EXPENSES */}
          {activeTab === "expenses" && (
            <div style={innerCard}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <div>
                  <div style={label}>Date</div>
                  <input style={input} type="date" value={expDate} onChange={(e) => setExpDate(e.target.value)} />
                </div>

                <div>
                  <div style={label}>Category</div>
                  <select style={input} value={expCategory} onChange={(e) => setExpCategory(e.target.value)}>
                    {[
                      "Payroll",
                      "Fuel",
                      "Software",
                      "Insurance",
                      "Marketing",
                      "Training",
                      "Equipment",
                      "Office",
                      "Bank fees",
                      "Other",
                    ].map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div style={label}>Amount (£)</div>
                  <input style={input} type="number" value={expAmount} onChange={(e) => setExpAmount(e.target.value)} placeholder="e.g. 45" />
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <div style={label}>Vendor / Payee (optional)</div>
                  <input style={input} value={expVendor} onChange={(e) => setExpVendor(e.target.value)} placeholder="e.g. Monzo, Uber, HMRC" />
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <div style={label}>Description (optional)</div>
                  <input style={input} value={expDescription} onChange={(e) => setExpDescription(e.target.value)} placeholder="e.g. Website hosting January" />
                </div>

                <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button onClick={addExpense} disabled={expSaving} style={{ ...btn("#2563eb"), opacity: expSaving ? 0.7 : 1 }}>
                    {expSaving ? "Saving…" : "Add expense"}
                  </button>

                  <div style={{ marginLeft: "auto", display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <div style={statPill("#fff7ed", "#9a3412")}>Expenses (month): {fmtGBP(expenseTotals)}</div>
                  </div>
                </div>

                {(expMsg.ok || expMsg.err) && (
                  <div style={{ gridColumn: "1 / -1", fontWeight: 900, fontSize: 13, color: expMsg.err ? "#dc2626" : "#166534" }}>
                    {expMsg.err ? `❌ ${expMsg.err}` : `✅ ${expMsg.ok}`}
                  </div>
                )}
              </div>

              <div style={{ marginTop: 16, overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 14, background: "#fff" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={th}>Date</th>
                      <th style={th}>Category</th>
                      <th style={th}>Vendor</th>
                      <th style={th}>Description</th>
                      <th style={{ ...th, textAlign: "right" }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredExpenses.map((e, idx) => (
                      <tr key={e.id} style={{ background: idx % 2 === 0 ? "#ffffff" : "#f9fafb" }}>
                        <td style={td}>{e.date || "—"}</td>
                        <td style={td}>{e.category || "—"}</td>
                        <td style={td}>{e.vendor || "—"}</td>
                        <td style={td}>{e.description || "—"}</td>
                        <td style={{ ...td, textAlign: "right", fontWeight: 900 }}>{fmtGBP(safeNum(e.amount))}</td>
                      </tr>
                    ))}

                    {filteredExpenses.length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ padding: 16, color: "#6b7280", fontWeight: 700 }}>
                          No expenses logged for this month.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}


{/* STAFF PAYROLL */}
{activeTab === "payroll" && (
  <div style={innerCard}>
    {/* Summary Metric Bar aligned with layout styles */}
    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12, marginBottom: 14 }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ fontWeight: 900, color: "#111827", fontSize: "15px" }}>
          Staff Payroll Summary
        </div>
        
        <div style={{ marginLeft: "auto", display: "flex", gap: 10, flexWrap: "wrap" }}>
          <div style={statPill("#ecfdf5", "#065f46")}>
            Total Checked Out Shifts: {staffPayrollSummary.reduce((acc, m) => acc + m.checkedOutShiftsCount, 0)}
          </div>
          <div style={statPill("#eff6ff", "#1e40af")}>
            Total Hours: {staffPayrollSummary.reduce((acc, m) => acc + m.totalHours, 0).toFixed(2)} hrs
          </div>
          <div style={statPill("#fdf2f8", "#9d174d")}>
            Gross Payroll Accrual: {fmtGBP(staffPayrollSummary.reduce((acc, m) => acc + m.totalPay, 0))}
          </div>
        </div>
      </div>
    </div>

    {/* Summary Data Table utilizing identical internal border structures */}
    <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 14, background: "#fff" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={th}>Staff Name</th>
            <th style={{ ...th, textAlign: "center" }}>Checked Out Shifts</th>
            <th style={{ ...th, textAlign: "center" }}>Total Hours</th>
            <th style={{ ...th, textAlign: "right" }}>Total Pay (£13/hr)</th>
          </tr>
        </thead>
        <tbody>
          {staffPayrollSummary.map((member, idx) => (
            <tr key={idx} style={{ background: idx % 2 === 0 ? "#ffffff" : "#f9fafb" }}>
              <td style={{ ...td, fontWeight: 700 }}>{member.name}</td>
              <td style={{ ...td, textAlign: "center" }}>{member.checkedOutShiftsCount}</td>
              <td style={{ ...td, textAlign: "center" }}>{member.totalHours.toFixed(2)} hrs</td>
              <td style={{ ...td, textAlign: "right", fontWeight: 900, color: "#16a34a" }}>
                {fmtGBP(member.totalPay)}
              </td>
            </tr>
          ))}

          {staffPayrollSummary.length === 0 && (
            <tr>
              <td colSpan={4} style={{ padding: 16, color: "#6b7280", fontWeight: 700, textAlign: "center" }}>
                No historic staff shift records found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
)}



          {/* PROFIT */}
          {activeTab === "profit" && (
            <div style={innerCard}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div style={{ border: "1px solid #e5e7eb", background: "#fff", borderRadius: 16, padding: 16 }}>
                  <div style={{ fontWeight: 900, color: "#111827", marginBottom: 8 }}>Monthly summary ({monthFilter})</div>
                  <div style={{ display: "grid", gap: 8, fontSize: 13, fontWeight: 800, color: "#374151" }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "#6b7280" }}>Revenue</span>
                      <span>{fmtGBP(profitSummary.revenue)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "#6b7280" }}>Expenses</span>
                      <span>{fmtGBP(profitSummary.expenses)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "#6b7280" }}>Profit</span>
                      <span style={{ color: profitSummary.profit >= 0 ? "#166534" : "#dc2626" }}>{fmtGBP(profitSummary.profit)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "#6b7280" }}>Margin</span>
                      <span>{profitSummary.margin.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>

                <div style={{ border: "1px solid #e5e7eb", background: "#fff", borderRadius: 16, padding: 16 }}>
                  <div style={{ fontWeight: 900, color: "#111827", marginBottom: 8 }}>Year summary ({profitSummary.selectedYear})</div>
                  <div style={{ display: "grid", gap: 8, fontSize: 13, fontWeight: 800, color: "#374151" }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "#6b7280" }}>Revenue (YTD)</span>
                      <span>{fmtGBP(profitSummary.ytdRevenue)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "#6b7280" }}>Expenses (YTD)</span>
                      <span>{fmtGBP(profitSummary.ytdExpenses)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "#6b7280" }}>Profit (YTD)</span>
                      <span style={{ color: profitSummary.ytdProfit >= 0 ? "#166534" : "#dc2626" }}>{fmtGBP(profitSummary.ytdProfit)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "#6b7280" }}>Margin (YTD)</span>
                      <span>{profitSummary.ytdMargin.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 14, color: "#6b7280", fontWeight: 700, fontSize: 12 }}>
                Tip: For cashflow, use the Invoices tab (Paid vs Outstanding). Profit here uses all invoices in the selected month.
              </div>
            </div>
          )}

          {/* CLIENTS */}
          {activeTab === "clients" && (
            <div style={innerCard}>
              <div style={{ fontWeight: 900, color: "#111827", marginBottom: 10 }}>Client analytics (starter)</div>
              <div style={{ color: "#6b7280", fontWeight: 700, fontSize: 13 }}>
                Add insights like: revenue by client, average invoice value, and late payment rate.
              </div>

              <div style={{ marginTop: 14, overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 14, background: "#fff" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={th}>Client</th>
                      <th style={{ ...th, textAlign: "right" }}>Invoices</th>
                      <th style={{ ...th, textAlign: "right" }}>Revenue</th>
                      <th style={{ ...th, textAlign: "right" }}>Paid</th>
                      <th style={{ ...th, textAlign: "right" }}>Outstanding</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const byClient = new Map();
                      filteredInvoices.forEach((inv) => {
                        const name = inv.clientName || inv.invoiceFor || "Unknown";
                        const cur = byClient.get(name) || { count: 0, rev: 0, paid: 0 };
                        const t = safeNum(inv.total ?? inv.subtotal);
                        cur.count += 1;
                        cur.rev += t;
                        if (inv.paid) cur.paid += t;
                        byClient.set(name, cur);
                      });
                      const rows = Array.from(byClient.entries())
                        .map(([name, v]) => ({ name, ...v, out: Math.max(0, v.rev - v.paid) }))
                        .sort((a, b) => b.rev - a.rev);

                      if (rows.length === 0) {
                        return (
                          <tr>
                            <td colSpan={5} style={{ padding: 16, color: "#6b7280", fontWeight: 700 }}>
                              No data for this month.
                            </td>
                          </tr>
                        );
                      }

                      return rows.map((r, idx) => (
                        <tr key={r.name} style={{ background: idx % 2 === 0 ? "#fff" : "#f9fafb" }}>
                          <td style={td}>{r.name}</td>
                          <td style={{ ...td, textAlign: "right" }}>{r.count}</td>
                          <td style={{ ...td, textAlign: "right", fontWeight: 900 }}>{fmtGBP(r.rev)}</td>
                          <td style={{ ...td, textAlign: "right" }}>{fmtGBP(r.paid)}</td>
                          <td style={{ ...td, textAlign: "right" }}>{fmtGBP(r.out)}</td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* OPERATIONS */}

						{/* STAFF / SHIFTS */}
			{activeTab === "operations" && (
			  <div style={innerCard}>
				<div style={{ fontWeight: 900, color: "#111827", marginBottom: 10 }}>Staff / Shift analytics</div>

				{/* Monthly KPIs */}
				<div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
				  <div style={statPill("#eff6ff", "#1d4ed8")}>Completed shifts (month): {completedShiftsInMonth.length}</div>
				  <div style={statPill("#f0fdf4", "#166534")}>Hours delivered (month): {shiftHoursInMonth.toFixed(2)}</div>
				  <div style={statPill("#fff7ed", "#9a3412")}>Avg hours / week delivered: {avgDeliveredHoursPerWeek.toFixed(2)} hrs </div>
				</div>


<div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop:"8px"}}>
  <div style={statPill("#eff6ff", "#1d4ed8")}>
    Shifts assigned ({periodMode}): {assignedShiftsInPeriod.length}
  </div>

<div style={statPill("#e0e7ff", "#3730a3")}>
  Weeks with assigned: {weeksWithAssignedInPeriod}
</div>


  <div style={statPill("#ecfeff", "#0e7490")}>
    Hours assigned ({periodMode}): {assignedHoursInPeriod.toFixed(2)}
  </div>

  <div style={statPill("#cffafe", "#155e75")}>
    Avg hours / week assigned: {avgAssignedHoursPerWeek.toFixed(2)} hrs
  </div>

  

  

  <div style={statPill("#fdf2f8", "#9d174d")}>
    Staff active ({periodMode}): {activeStaffThisMonth}
  </div>
</div>






				{/* Year-to-date summary */}
				<div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
				  <div style={{ border: "1px solid #e5e7eb", background: "#fff", borderRadius: 16, padding: 16 }}>
					<div style={{ fontWeight: 700, color: "#111827", marginBottom: 8 }}>
					  Year-to-date ({staffYtdSummary.selectedYear})
					</div>

					<div style={{ display: "grid", gap: 8, fontSize: 13, fontWeight: 800, color: "#374151" }}>
					  <div style={{ display: "flex", justifyContent: "space-between" }}>
						<span style={{ color: "#6b7280" }}>Completed shifts</span>
						<span>{staffYtdSummary.completedCount}</span>
					  </div>
					  <div style={{ display: "flex", justifyContent: "space-between" }}>
						<span style={{ color: "#6b7280" }}>Hours delivered (YTD)</span>
						<span>{staffYtdSummary.hoursYtd.toFixed(2)}</span>
					  </div>
					  <div style={{ display: "flex", justifyContent: "space-between" }}>
						<span style={{ color: "#6b7280" }}>Avg hours / week (YTD)</span>
								<span>{staffYtdSummary.avgHoursPerWeekYtd.toFixed(2)} hrs</span>

					  </div>
					</div>
				  </div>

				  <div style={{ border: "1px solid #e5e7eb", background: "#fff", borderRadius: 16, padding: 16 }}>
					<div style={{ fontWeight: 800, color: "#111827", marginBottom: 8 }}>Notes</div>
					<div style={{ color: "#6b7280", fontWeight: 500, fontSize: 13, lineHeight: 1.6 }}>
					  This analytics view provides an overview of staff activity, shift allocation, and hours delivered across selected periods.

				The information supports operational planning by highlighting workforce utilisation, consistency of staffing, and trends in service delivery.

					All figures are generated from recorded shifts and assignments and reflect actual activity within the system.
					</div>
				  </div>
				</div>

				{/* Top staff table */}
				<div style={{ marginTop: 14, overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 14, background: "#fff" }}>
				  <table style={{ width: "100%", borderCollapse: "collapse" }}>
					<thead>
					  <tr>
						<th style={th}>Staff</th>
						<th style={{ ...th, textAlign: "right" }}>Shifts (YTD)</th>
						<th style={{ ...th, textAlign: "right" }}>Hours (YTD)</th>
					  </tr>
					</thead>
					<tbody>
					  {staffYtdSummary.topStaff.map((r, idx) => (
						<tr key={`${r.staffId || r.staffName}_${idx}`} style={{ background: idx % 2 === 0 ? "#fff" : "#f9fafb" }}>
						  <td style={td}>{r.staffName || "Unknown"}</td>
						  <td style={{ ...td, textAlign: "right" }}>{r.shifts}</td>
						  <td style={{ ...td, textAlign: "right", fontWeight: 900 }}>{r.hours.toFixed(2)}</td>
						</tr>
					  ))}

					  {staffYtdSummary.topStaff.length === 0 && (
						<tr>
						  <td colSpan={3} style={{ padding: 16, color: "#6b7280", fontWeight: 700 }}>
							No completed shifts found for the selected year.
						  </td>
						</tr>
					  )}
					</tbody>
				  </table>
				</div>
			  </div>
			)}








          {/* EXPORTS */}
          {activeTab === "exports" && (
            <div style={innerCard}>
              <div style={{ fontWeight: 900, color: "#111827", marginBottom: 10 }}>Exports</div>
              <div style={{ color: "#6b7280", fontWeight: 700, fontSize: 13, marginBottom: 12 }}>
                Common exports: invoices CSV, expenses CSV, profit summary for accountant.
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  style={btn("#111827")}
                  onClick={() => alert("Export wiring will be added next (CSV download).")}
                >
                  Export invoices (CSV)
                </button>
                <button
                  style={btn("#111827")}
                  onClick={() => alert("Export wiring will be added next (CSV download).")}
                >
                  Export expenses (CSV)
                </button>
                <button
                  style={btn("#111827")}
                  onClick={() => alert("Export wiring will be added next (CSV download).")}
                >
                  Export profit summary
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer note */}
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 4px 18px", color: "rgba(255,255,255,0.75)", fontSize: 12, fontWeight: 700 }}>
        
      </div>
    </div>
  );
}
